import React, { useContext, useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Autocomplete, Chip, Divider, TextField } from '@mui/material';
import { AuthContext } from 'authentication/AuthContext';
import renderInput from 'feature/inventory-management/reconsignments/edit/renderUtils';
import {
  getSingleFilteredOption,
  handleSingleOptionEnterToggle,
} from 'feature/loading-management/utils/autocompleteEnterToggle';
import {
  useGetFilterLocationsListQuery,
  useGetUniqueAffiliateProductsListQuery,
  useGetUniqueAffiliateSuppliersListQuery,
} from './supplierAuthorizationsEndpoints';

const renderCollapsedTags = selectedOptions => {
  if (!selectedOptions.length) {
    return null;
  }

  const firstLabel = selectedOptions[0]?.label ?? selectedOptions[0]?.value ?? '';

  return (
    <>
      <span
        style={{
          display: 'inline-block',
          marginLeft: 5,
          marginRight: 8,
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {firstLabel}
      </span>
      {selectedOptions.length > 1 && (
        <Chip label={`+${selectedOptions.length - 1}`} size="small" variant="autocomplete" />
      )}
    </>
  );
};

const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
};

const getSupplierIdName = supplier =>
  supplier?.companyCustomerIdName ?? supplier?.idName ?? supplier?.customerIdName ?? null;

const getSupplierLabel = supplier => supplier?.label ?? supplier?.nameLong;
const getOptionLabel = option => option?.label ?? '';
const isSupplierOptionEqual = (option, value) =>
  option?.companyCustomerIdName === value?.companyCustomerIdName &&
  option?.dataCompanyId === value?.dataCompanyId;
const isValueOptionEqual = (option, value) => option?.value === value?.value;

const mapSupplierOption = supplier => {
  const companyCustomerIdName = getSupplierIdName(supplier);
  const dataCompanyId = supplier?.dataCompanyId;

  if (!companyCustomerIdName || dataCompanyId === null || dataCompanyId === undefined) {
    return null;
  }

  return {
    companyCustomerIdName,
    dataCompanyId,
    label: getSupplierLabel(supplier),
    value: companyCustomerIdName,
  };
};

const SupplierAuthorizationsFilters = ({
  affiliateFilter,
  isExporting,
  locationFilter,
  setAffiliateFilter,
  setLocationFilter,
  setSupplierFilter,
  supplierFilter,
  productFilter,
  setProductFilter,
  statusFilter,
  setStatusFilter,
}) => {
  const { affiliates = [] } = useContext(AuthContext);
  const [supplierInputValue, setSupplierInputValue] = useState('');
  const [locationInputValue, setLocationInputValue] = useState('');
  const [productInputValue, setProductInputValue] = useState('');

  const affiliateOptions = useMemo(
    () => affiliates.filter(affiliate => affiliate?.inventoryOwner),
    [affiliates]
  );

  const supplierParams = {
    'Affiliate.CompanyCustomerIdName': affiliateFilter?.customerIdName,
    'Affiliate.DataCompanyId': affiliateFilter?.dataCompanyId,
  };

  const { data: supplierData = [], isLoading: isSuppliersLoading } =
    useGetUniqueAffiliateSuppliersListQuery(supplierParams, {
      skip: !affiliateFilter?.customerIdName || !affiliateFilter?.dataCompanyId,
    });

  const supplierOptions = supplierData.map(mapSupplierOption).filter(Boolean);

  const locationParams = {
    DataCompanyId: affiliateFilter?.dataCompanyId,
  };

  const { data: locationsData = [], isLoading: isLocationsLoading } =
    useGetFilterLocationsListQuery(locationParams, {
      skip: !affiliateFilter?.dataCompanyId,
    });

  const locationOptions = locationsData.map(({ locationDisplayName, locationId }) => ({
    label: locationDisplayName,
    value: locationId,
  }));

  const { data: productsData = [], isLoading: isProductsLoading } =
    useGetUniqueAffiliateProductsListQuery(supplierParams, {
      skip: !affiliateFilter?.customerIdName || !affiliateFilter?.dataCompanyId,
    });

  const productOptions = productsData.map(({ nameLong, productId }) => ({
    label: nameLong,
    value: productId,
  }));

  const supplierSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(supplierOptions, supplierInputValue, getSupplierLabel),
    [supplierInputValue, supplierOptions]
  );

  const locationSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(locationOptions, locationInputValue, getOptionLabel),
    [locationInputValue, locationOptions]
  );

  const productSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(productOptions, productInputValue, getOptionLabel),
    [productInputValue, productOptions]
  );

  useEffect(() => {
    if (affiliateOptions.length > 0 && !affiliateFilter) {
      setAffiliateFilter(affiliateOptions[0]);
    }
  }, [affiliateFilter, affiliateOptions, setAffiliateFilter]);

  return (
    <>
      {/* Affiliate */}
      {affiliateOptions.length > 1 && (
        <Autocomplete
          autoHighlight
          disableClearable
          disabled={isExporting}
          disablePortal
          getOptionLabel={option => option?.customerNameLong ?? ''}
          id="affiliate-auth-filter"
          isOptionEqualToValue={(option, value) =>
            option?.customerIdName === value?.customerIdName &&
            option?.dataCompanyId === value?.dataCompanyId
          }
          onChange={(event, newValue) => setAffiliateFilter(newValue)}
          options={affiliateOptions}
          renderInput={renderInput({
            label: 'Affiliate',
            length: affiliateOptions.length,
            loading: false,
          })}
          value={affiliateFilter || affiliateOptions[0] || null}
        />
      )}
      {affiliateOptions.length > 1 && <Divider />}
      {/* Supplier */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={isExporting}
        disablePortal
        getOptionLabel={option => getSupplierLabel(option)}
        id="supplier-auth-filter"
        inputValue={supplierInputValue}
        isOptionEqualToValue={isSupplierOptionEqual}
        multiple
        onChange={(event, newValue) => setSupplierFilter(newValue)}
        onInputChange={(event, newInputValue) => setSupplierInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            supplierSingleFilteredOption,
            supplierFilter,
            setSupplierFilter,
            isSupplierOptionEqual
          )
        }
        openOnFocus
        options={supplierOptions}
        renderInput={renderInput({
          label: 'Suppliers',
          length: supplierOptions.length,
          loading: isSuppliersLoading,
          selectedCount: supplierFilter.length,
        })}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={`${option.companyCustomerIdName}-${option.dataCompanyId}`}>
            {getSupplierLabel(option)}
          </li>
        )}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={supplierFilter}
      />
      {/* Locations */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={isExporting}
        disablePortal
        id="location-auth-filter"
        inputValue={locationInputValue}
        isOptionEqualToValue={isValueOptionEqual}
        multiple
        onChange={(event, newValue) => setLocationFilter(newValue)}
        onInputChange={(event, newInputValue) => setLocationInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            locationSingleFilteredOption,
            locationFilter,
            setLocationFilter,
            isValueOptionEqual
          )
        }
        openOnFocus
        options={locationOptions}
        renderInput={renderInput({
          label: 'Locations',
          length: locationOptions.length,
          loading: isLocationsLoading,
          selectedCount: locationFilter.length,
        })}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.locationId ?? option.value}>
            {option.label}
          </li>
        )}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={locationFilter}
      />
      {/* Product */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disabled={isExporting}
        disablePortal
        id="product-auth-filter"
        inputValue={productInputValue}
        isOptionEqualToValue={isValueOptionEqual}
        multiple
        onChange={(event, newValue) => setProductFilter(newValue)}
        onInputChange={(event, newInputValue) => setProductInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            productSingleFilteredOption,
            productFilter,
            setProductFilter,
            isValueOptionEqual
          )
        }
        openOnFocus
        options={productOptions}
        renderInput={renderInput({
          label: 'Products',
          length: productOptions.length,
          loading: isProductsLoading,
          selectedCount: productFilter.length,
        })}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={productFilter}
      />
      {/* Status */}
      <Autocomplete
        autoHighlight
        disableClearable
        disabled={isExporting}
        disablePortal
        id="status-auth-filter"
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        onChange={(event, newValue) => setStatusFilter(newValue)}
        options={[
          { label: 'All On', value: 'Allon' },
          { label: 'All Off', value: 'Alloff' },
          { label: 'All Statuses', value: 'Allstatuses' },
        ]}
        renderInput={params => <TextField {...params} disabled={isExporting} label="Status" />}
        value={statusFilter}
      />
    </>
  );
};

SupplierAuthorizationsFilters.propTypes = {
  affiliateFilter: shape({
    customerIdName: string,
    customerNameLong: string,
    dataCompanyId: string,
  }),
  isExporting: bool,
  locationFilter: arrayOf(
    shape({
      label: string,
      value: string,
    })
  ),
  productFilter: arrayOf(
    shape({
      label: string,
      value: string,
    })
  ),
  setAffiliateFilter: func.isRequired,
  setLocationFilter: func.isRequired,
  setProductFilter: func.isRequired,
  setStatusFilter: func.isRequired,
  setSupplierFilter: func.isRequired,
  statusFilter: shape({
    label: string,
    value: string,
  }),
  supplierFilter: arrayOf(
    shape({
      companyCustomerIdName: string,
      dataCompanyId: string,
      label: string,
      value: string,
    })
  ),
};

SupplierAuthorizationsFilters.defaultProps = {
  affiliateFilter: null,
  isExporting: false,
  locationFilter: [],
  productFilter: [],
  statusFilter: { label: 'All On', value: 'Allon' },
  supplierFilter: [],
};

export default SupplierAuthorizationsFilters;
